import React from 'react';

type InstantStep = {
  title: string;
  eyebrow: string;
  copy: string;
  image: string;
  alt: string;
  tone?: 'blue' | 'green' | 'amber' | 'red';
};

const instantSteps: InstantStep[] = [
  {
    eyebrow: 'Step 1',
    title: 'Go online when you are ready',
    copy: 'Swipe online to receive instant trip requests. Stay offline when you are not available.',
    image: '/images/driver-how-to/01-go-online.jpeg',
    alt: 'Driver App home screen with the online swipe control',
    tone: 'green',
  },
  {
    eyebrow: 'Step 2',
    title: 'Check the new trip request',
    copy: 'Look at the payout, pickup, drop-off, distance, and time. Accept the job only when you can do it.',
    image: '/images/driver-how-to/02-new-trip-request-generic.png',
    alt: 'Instant booking request showing payout, distance, time, pickup, and accept button',
    tone: 'blue',
  },
  {
    eyebrow: 'Step 3',
    title: 'Drive to pickup',
    copy: 'Open navigation and head to the pickup. You can call or message the customer if you need help.',
    image: '/images/driver-how-to/03-get-to-pickup-generic.png',
    alt: 'Trip progress screen showing get to pickup and confirm loading',
    tone: 'blue',
  },
  {
    eyebrow: 'Step 4',
    title: 'Check the load before loading',
    copy: 'Before loading starts, check that the real load matches the request. If it matches, continue.',
    image: '/images/driver-how-to/04-load-match-check-generic.png',
    alt: 'Load match confirmation asking if the load matches the request',
    tone: 'amber',
  },
  {
    eyebrow: 'Step 5',
    title: 'Adjust the fare if the load changed',
    copy: 'Did we underprice that load? If it is bigger, heavier, harder to access, or needs a different amount, adjust the fare before loading.',
    image: '/images/driver-how-to/05-adjust-fare-generic.png',
    alt: 'Fare adjustment screen for a load that needs a new fare',
    tone: 'amber',
  },
  {
    eyebrow: 'Step 6',
    title: 'Swipe enroute after loading',
    copy: 'Once the load is on, do not stay on Loading. Swipe enroute so the trip moves forward correctly.',
    image: '/images/driver-how-to/06-confirm-enroute-generic.png',
    alt: 'Trip progress screen warning not to stay on Loading and showing confirm enroute',
    tone: 'red',
  },
  {
    eyebrow: 'Step 7',
    title: 'Drive to drop-off',
    copy: 'Open navigation and drive to the destination. When you arrive, swipe unloading before offloading starts.',
    image: '/images/driver-how-to/07-confirm-unloading-generic.png',
    alt: 'Trip progress screen showing deliver to dropoff and confirm unloading',
    tone: 'green',
  },
  {
    eyebrow: 'Step 8',
    title: 'Close the trip after offload',
    copy: 'After offloading is done, swipe delivered. New trips unlock once this trip is closed.',
    image: '/images/driver-how-to/08-close-trip-generic.png',
    alt: 'Trip progress screen showing close this trip and confirm delivered',
    tone: 'red',
  },
  {
    eyebrow: 'Step 9',
    title: 'Take proof of delivery photos',
    copy: 'Capture the required delivery photos, then mark the delivery complete.',
    image: '/images/driver-how-to/09-proof-of-delivery-furniture-v2.png',
    alt: 'Proof of delivery screen showing required photos and mark delivered button',
    tone: 'blue',
  },
  {
    eyebrow: 'Step 10',
    title: 'Rate the customer',
    copy: 'Rating is optional. It helps ShiftWagon improve the delivery experience.',
    image: '/images/driver-how-to/10-rate-customer.jpeg',
    alt: 'Rate customer screen with star rating and comment box',
    tone: 'blue',
  },
];

const DriverInstantTripsGuide: React.FC = () => {
  return (
    <section className="driver-how-section">
      <div className="container">
        <div className="driver-how-section-head">
          <span className="sw-eyebrow">How to use Driver App</span>
          <h2>Instant Trips: from online to delivered.</h2>
          <p>
            Instant Trips come in while you are online. Follow each screen in order and keep the
            delivery moving as the work changes.
          </p>
        </div>

        <div className="driver-how-timeline">
          {instantSteps.map((step, index) => (
            <article className={`driver-how-step driver-how-step-${step.tone ?? 'blue'}`} key={step.title}>
              <div className="driver-how-step-copy">
                <div className="driver-how-step-topline">
                  <span className="driver-how-step-number">{String(index + 1).padStart(2, '0')}</span>
                  <span className="sw-eyebrow">{step.eyebrow}</span>
                </div>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </div>

              <figure className="driver-how-phone-shot">
                <img src={step.image} alt={step.alt} loading={index < 2 ? 'eager' : 'lazy'} />
              </figure>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DriverInstantTripsGuide;
